<script setup lang="ts">
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Upload } from 'lucide-vue-next'
import type { Scene } from '@/stores/scenes'
import type { SpotifyNode } from '@/stores/spotifyNodes'
import type { MusicNode } from '@/stores/musicNodes'
import type { PlaylistNode } from '@/stores/playlistNodes'
import type { GraphContainer } from '@/stores/graphContainers'

interface DialogState {
  isOpen: { value: boolean }
  close: () => void
}

interface Props {
  isMobile: boolean
  // Connect dialog
  dialogs: {
    connectConfirm: DialogState
    disconnectConfirm: DialogState
    editScene: DialogState
    deleteScene: DialogState
    addSpotify: DialogState
    editSpotify: DialogState
    deleteSpotify: DialogState
    addMusic: DialogState
    editMusic: DialogState
    deleteMusic: DialogState
    addPlaylist: DialogState
    deletePlaylist: DialogState
    deleteContainer: DialogState
  }
  // Connect state
  childSceneName: string
  parentSceneName: string
  descendantCount: number
  isConnecting: boolean
  // Disconnect state
  sceneToDisconnectName: string
  descendantCountToDisconnect: number
  isDisconnecting: boolean
  // Scene state
  sceneToDelete: Scene | null
  editSceneName: string
  editSceneDescription: string
  isEditingScene: boolean
  isDeletingScene: boolean
  // Spotify state
  spotifyUrlInput: string
  spotifyNodeToDelete: SpotifyNode | null
  editSpotifyName: string
  editSpotifyVolume: number[]
  isCreatingSpotify: boolean
  isDeletingSpotify: boolean
  // Music state
  musicNodeToDelete: MusicNode | null
  editMusicName: string
  editMusicVolume: number[]
  isCreatingMusic: boolean
  isDeletingMusic: boolean
  // Playlist state
  playlistNameInput?: string
  playlistNodeToDelete?: PlaylistNode | null
  isCreatingPlaylist?: boolean
  isDeletingPlaylist?: boolean
  // Container state
  containerToDelete: GraphContainer | null
}

const props = defineProps<Props>()

const emit = defineEmits([
  'update:editSceneName',
  'update:editSceneDescription',
  'update:spotifyUrlInput',
  'update:editSpotifyName',
  'update:editSpotifyVolume',
  'update:editMusicName',
  'update:editMusicVolume',
  'update:playlistNameInput',
  'confirmConnect',
  'confirmDisconnect',
  'confirmEditScene',
  'executeDeleteScene',
  'createSpotifyNode',
  'confirmEditSpotify',
  'executeDeleteSpotify',
  'triggerMusicFileInput',
  'handleMusicFileDrop',
  'confirmEditMusic',
  'executeDeleteMusic',
  'createPlaylistNode',
  'executeDeletePlaylist',
  'executeDeleteContainer',
])

const dialogClass = (base: string) => props.isMobile 
  ? 'w-screen h-screen max-w-none m-0 rounded-none' 
  : base
</script>

<template>
  <!-- Connect Confirmation Dialog -->
  <Dialog :open="dialogs.connectConfirm.isOpen.value" @update:open="dialogs.connectConfirm.close()">
    <DialogContent :class="dialogClass('sm:max-w-[500px]')">
      <DialogHeader>
        <DialogTitle>Create Parent Relationship</DialogTitle>
        <DialogDescription>
          Connect "{{ childSceneName }}" as a child of "{{ parentSceneName }}"?
        </DialogDescription>
      </DialogHeader>
      
      <div class="space-y-4">
        <div class="rounded-lg border border-primary/50 bg-primary/10 p-4">
          <p class="text-sm font-medium mb-2">📦 Config Inheritance</p>
          <p class="text-sm text-muted-foreground">
            The child scene will automatically inherit light and display configurations from the parent.
          </p>
        </div>

        <div v-if="descendantCount > 0" class="rounded-lg border border-orange-500/50 bg-orange-500/10 p-4">
          <p class="text-sm font-medium text-orange-600 dark:text-orange-400 mb-2">⚠️ Note</p>
          <p class="text-sm text-muted-foreground">
            This scene has {{ descendantCount }} child scene{{ descendantCount > 1 ? 's' : '' }} that will move with it.
          </p>
        </div>

        <div class="flex justify-end gap-2">
          <Button variant="outline" @click="dialogs.connectConfirm.close()">Cancel</Button>
          <Button @click="emit('confirmConnect')" :disabled="isConnecting">
            {{ isConnecting ? 'Connecting...' : 'Create Connection' }}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>

  <!-- Disconnect Confirmation Dialog -->
  <Dialog :open="dialogs.disconnectConfirm.isOpen.value" @update:open="dialogs.disconnectConfirm.close()">
    <DialogContent :class="dialogClass('sm:max-w-[500px]')">
      <DialogHeader>
        <DialogTitle>Disconnect from Parent</DialogTitle>
        <DialogDescription>
          Make "{{ sceneToDisconnectName }}" a root-level scene?
        </DialogDescription>
      </DialogHeader>
      
      <div class="space-y-4">
        <div class="rounded-lg border border-orange-500/50 bg-orange-500/10 p-4">
          <p class="text-sm font-medium text-orange-600 dark:text-orange-400 mb-2">⚠️ Config Inheritance</p>
          <p class="text-sm text-muted-foreground">
            This scene will no longer inherit configurations from its parent.
          </p>
        </div>

        <div v-if="descendantCountToDisconnect > 0" class="rounded-lg border border-primary/50 bg-primary/10 p-4">
          <p class="text-sm font-medium mb-2">📦 Note</p>
          <p class="text-sm text-muted-foreground">
            This scene has {{ descendantCountToDisconnect }} child scene{{ descendantCountToDisconnect > 1 ? 's' : '' }} that will move with it.
          </p>
        </div>

        <div class="flex justify-end gap-2">
          <Button variant="outline" @click="dialogs.disconnectConfirm.close()">Cancel</Button>
          <Button variant="destructive" @click="emit('confirmDisconnect')" :disabled="isDisconnecting">
            {{ isDisconnecting ? 'Disconnecting...' : 'Disconnect' }}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>

  <!-- Edit Scene Dialog -->
  <Dialog :open="dialogs.editScene.isOpen.value" @update:open="dialogs.editScene.close()">
    <DialogContent :class="dialogClass('sm:max-w-[500px]')">
      <DialogHeader>
        <DialogTitle>Edit Scene</DialogTitle>
        <DialogDescription>Update the scene name and description</DialogDescription>
      </DialogHeader>
      
      <div class="space-y-4">
        <div>
          <Label for="edit-scene-name">Scene Name</Label>
          <Input
            id="edit-scene-name"
            :model-value="editSceneName"
            @update:model-value="emit('update:editSceneName', $event)"
            placeholder="Enter scene name"
            @keydown.enter="emit('confirmEditScene')"
          />
        </div>

        <div>
          <Label for="edit-scene-description">Description (optional)</Label>
          <Input
            id="edit-scene-description"
            :model-value="editSceneDescription"
            @update:model-value="emit('update:editSceneDescription', $event)"
            placeholder="Enter scene description"
          />
        </div>

        <div class="flex justify-end gap-2">
          <Button variant="outline" @click="dialogs.editScene.close()">Cancel</Button>
          <Button @click="emit('confirmEditScene')" :disabled="isEditingScene || !editSceneName.trim()">
            {{ isEditingScene ? 'Saving...' : 'Save' }}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>

  <!-- Delete Scene Dialog -->
  <Dialog :open="dialogs.deleteScene.isOpen.value" @update:open="dialogs.deleteScene.close()">
    <DialogContent :class="dialogClass('sm:max-w-[500px]')">
      <DialogHeader>
        <DialogTitle>Delete Scene</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete "{{ sceneToDelete?.name }}"? This action cannot be undone.
        </DialogDescription>
      </DialogHeader>
      
      <div class="flex justify-end gap-2">
        <Button variant="outline" @click="dialogs.deleteScene.close()">Cancel</Button>
        <Button variant="destructive" @click="emit('executeDeleteScene')" :disabled="isDeletingScene">
          {{ isDeletingScene ? 'Deleting...' : 'Delete' }}
        </Button>
      </div>
    </DialogContent>
  </Dialog>

  <!-- Add Spotify Dialog -->
  <Dialog :open="dialogs.addSpotify.isOpen.value" @update:open="dialogs.addSpotify.close()">
    <DialogContent :class="dialogClass('sm:max-w-[500px]')">
      <DialogHeader>
        <DialogTitle>Add Spotify</DialogTitle>
        <DialogDescription>Paste a Spotify track or playlist URL to add it to the graph.</DialogDescription>
      </DialogHeader>
      
      <div class="space-y-4">
        <div>
          <Label for="spotify-url">Spotify URL</Label>
          <Input
            id="spotify-url"
            :model-value="spotifyUrlInput"
            @update:model-value="emit('update:spotifyUrlInput', $event)"
            placeholder="https://open.spotify.com/track/..."
            @keydown.enter="emit('createSpotifyNode')"
          />
        </div>

        <div class="flex justify-end gap-2">
          <Button variant="outline" @click="dialogs.addSpotify.close()">Cancel</Button>
          <Button @click="emit('createSpotifyNode')" :disabled="isCreatingSpotify || !spotifyUrlInput.trim()">
            {{ isCreatingSpotify ? 'Adding...' : 'Add' }}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>

  <!-- Edit Spotify Dialog -->
  <Dialog :open="dialogs.editSpotify.isOpen.value" @update:open="dialogs.editSpotify.close()">
    <DialogContent :class="dialogClass('sm:max-w-[500px]')">
      <DialogHeader>
        <DialogTitle>Edit Spotify Node</DialogTitle>
        <DialogDescription>Update the Spotify node settings</DialogDescription>
      </DialogHeader>
      
      <div class="space-y-4">
        <div>
          <Label for="edit-spotify-name">Name</Label>
          <Input
            id="edit-spotify-name"
            :model-value="editSpotifyName"
            @update:model-value="emit('update:editSpotifyName', $event)"
            placeholder="Enter name"
          />
        </div>

        <div>
          <Label>Volume: {{ editSpotifyVolume[0] }}%</Label>
          <Slider
            :model-value="editSpotifyVolume"
            @update:model-value="emit('update:editSpotifyVolume', $event)"
            :max="100"
            :step="1"
            class="mt-2"
          />
        </div>

        <div class="flex justify-end gap-2">
          <Button variant="outline" @click="dialogs.editSpotify.close()">Cancel</Button>
          <Button @click="emit('confirmEditSpotify')" :disabled="!editSpotifyName.trim()">Save</Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>

  <!-- Delete Spotify Dialog -->
  <Dialog :open="dialogs.deleteSpotify.isOpen.value" @update:open="dialogs.deleteSpotify.close()">
    <DialogContent :class="dialogClass('sm:max-w-[500px]')">
      <DialogHeader>
        <DialogTitle>Delete Spotify Node</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete "{{ spotifyNodeToDelete?.name }}"? This action cannot be undone.
        </DialogDescription>
      </DialogHeader>
      
      <div class="flex justify-end gap-2">
        <Button variant="outline" @click="dialogs.deleteSpotify.close()">Cancel</Button>
        <Button variant="destructive" @click="emit('executeDeleteSpotify')" :disabled="isDeletingSpotify">
          {{ isDeletingSpotify ? 'Deleting...' : 'Delete' }}
        </Button>
      </div>
    </DialogContent>
  </Dialog>

  <!-- Add Music Dialog -->
  <Dialog :open="dialogs.addMusic.isOpen.value" @update:open="dialogs.addMusic.close()">
    <DialogContent :class="dialogClass('sm:max-w-[500px]')">
      <DialogHeader>
        <DialogTitle>Add Sound</DialogTitle>
        <DialogDescription>Upload an audio file to add it to the graph.</DialogDescription>
      </DialogHeader>
      
      <div class="space-y-4">
        <div 
          class="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
          @click="emit('triggerMusicFileInput')"
          @dragover.prevent
          @drop.prevent="emit('handleMusicFileDrop', $event)"
        >
          <Upload class="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
          <p class="text-sm text-muted-foreground">
            Click to upload or drag and drop
          </p>
          <p class="text-xs text-muted-foreground/60 mt-1">
            MP3, WAV, OGG, FLAC (max 50MB)
          </p>
        </div>

        <div class="flex justify-end gap-2">
          <Button variant="outline" @click="dialogs.addMusic.close()">Cancel</Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>

  <!-- Edit Music Dialog -->
  <Dialog :open="dialogs.editMusic.isOpen.value" @update:open="dialogs.editMusic.close()">
    <DialogContent :class="dialogClass('sm:max-w-[500px]')">
      <DialogHeader>
        <DialogTitle>Edit Sound</DialogTitle>
        <DialogDescription>Update the sound settings</DialogDescription>
      </DialogHeader>
      
      <div class="space-y-4">
        <div>
          <Label for="edit-music-name">Name</Label>
          <Input
            id="edit-music-name"
            :model-value="editMusicName"
            @update:model-value="emit('update:editMusicName', $event)"
            placeholder="Enter name"
          />
        </div>

        <div>
          <Label>Volume: {{ editMusicVolume[0] }}%</Label>
          <Slider
            :model-value="editMusicVolume"
            @update:model-value="emit('update:editMusicVolume', $event)"
            :max="100"
            :step="1"
            class="mt-2"
          />
        </div>

        <div class="flex justify-end gap-2">
          <Button variant="outline" @click="dialogs.editMusic.close()">Cancel</Button>
          <Button @click="emit('confirmEditMusic')" :disabled="!editMusicName.trim()">Save</Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>

  <!-- Delete Music Dialog -->
  <Dialog :open="dialogs.deleteMusic.isOpen.value" @update:open="dialogs.deleteMusic.close()">
    <DialogContent :class="dialogClass('sm:max-w-[500px]')">
      <DialogHeader>
        <DialogTitle>Delete Sound</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete "{{ musicNodeToDelete?.name }}"? This action cannot be undone.
        </DialogDescription>
      </DialogHeader>
      
      <div class="flex justify-end gap-2">
        <Button variant="outline" @click="dialogs.deleteMusic.close()">Cancel</Button>
        <Button variant="destructive" @click="emit('executeDeleteMusic')" :disabled="isDeletingMusic">
          {{ isDeletingMusic ? 'Deleting...' : 'Delete' }}
        </Button>
      </div>
    </DialogContent>
  </Dialog>

  <!-- Delete Container Dialog -->
  <Dialog :open="dialogs.deleteContainer.isOpen.value" @update:open="dialogs.deleteContainer.close()">
    <DialogContent :class="dialogClass('sm:max-w-[500px]')">
      <DialogHeader>
        <DialogTitle>Delete Container</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete "{{ containerToDelete?.name }}"? This action cannot be undone.
        </DialogDescription>
      </DialogHeader>
      
      <div class="flex justify-end gap-2">
        <Button variant="outline" @click="dialogs.deleteContainer.close()">Cancel</Button>
        <Button variant="destructive" @click="emit('executeDeleteContainer')">
          Delete
        </Button>
      </div>
    </DialogContent>
  </Dialog>

  <!-- Add Playlist Dialog -->
  <Dialog :open="dialogs.addPlaylist.isOpen.value" @update:open="dialogs.addPlaylist.close()">
    <DialogContent :class="dialogClass('sm:max-w-[500px]')">
      <DialogHeader>
        <DialogTitle>Add Playlist</DialogTitle>
        <DialogDescription>Create a new playlist to group multiple audio tracks.</DialogDescription>
      </DialogHeader>
      
      <div class="space-y-4">
        <div>
          <Label for="playlist-name">Playlist Name</Label>
          <Input
            id="playlist-name"
            :model-value="playlistNameInput"
            @update:model-value="emit('update:playlistNameInput', $event)"
            placeholder="My Playlist"
            @keydown.enter="emit('createPlaylistNode')"
          />
        </div>

        <div class="flex justify-end gap-2">
          <Button variant="outline" @click="dialogs.addPlaylist.close()">Cancel</Button>
          <Button @click="emit('createPlaylistNode')" :disabled="isCreatingPlaylist || !playlistNameInput?.trim()">
            {{ isCreatingPlaylist ? 'Creating...' : 'Create' }}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>

  <!-- Delete Playlist Dialog -->
  <Dialog :open="dialogs.deletePlaylist.isOpen.value" @update:open="dialogs.deletePlaylist.close()">
    <DialogContent :class="dialogClass('sm:max-w-[500px]')">
      <DialogHeader>
        <DialogTitle>Delete Playlist</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete "{{ playlistNodeToDelete?.name }}"? 
          Music nodes inside will be kept but removed from the playlist.
        </DialogDescription>
      </DialogHeader>
      
      <div class="flex justify-end gap-2">
        <Button variant="outline" @click="dialogs.deletePlaylist.close()">Cancel</Button>
        <Button variant="destructive" @click="emit('executeDeletePlaylist')" :disabled="isDeletingPlaylist">
          {{ isDeletingPlaylist ? 'Deleting...' : 'Delete' }}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
